import Logo from '$/public/logo/logo.svg'
import {
  CodeSnippet,
  Container,
  Image,
  Link,
  Parse,
  Section
} from '@/components/Core'
import { SectionProps } from '@/components/Core/Section'
import GradientBg from '@/components/Layout/GradientBg'
import Button from '@/components/Ui/Button'
import LayoutFragment from '@/lib/fragments/layout'
import { Lib } from '@/types'
import clsx from 'clsx'
import { getTranslations } from 'next-intl/server'

export interface FooterProps extends SectionProps {
  data: Lib.FragmentOf<typeof LayoutFragment> | null
}

const Footer = async ({ data, ...props }: FooterProps) => {
  const t = await getTranslations()

  return (
    <Section as="footer" {...props} mainWrapper={false} className="">
      <div className={'footerTop relative overflow-hidden'}>
        <GradientBg />
        <div className="main-wrapper relative text-white">
          <Container>
            <div className="gap-gutter py-vertical-main md:py-vertical-main flex grid-rows-1 flex-col border-b border-white/20 pb-12 md:grid md:grid-cols-2">
              <div className="max-w-[11.25rem]">
                <Logo />
              </div>
              <div className="gap-vertical-small flex flex-col">
                <div className="text-large leading-tight font-semibold">
                  <Parse html={data?.footerDescription} />
                </div>
                <div>
                  <Button
                    data={data?.h24Link}
                    icon="ic:baseline-local-phone"
                    color="invert"
                    iconPosition="left"
                  />
                </div>
              </div>
            </div>
            <div className="gap-gutter gap-y-vertical-main py-vertical-main sm:py-vertical-small text-small grid grid-cols-2 lg:grid-cols-4">
              {data?.footerColumns?.map((column, index) => (
                <div className="max-sm:last:col-span-2" key={index}>
                  <h2 className="!text-style-base mb-sm">{column.title}</h2>
                  <div className="prose text-small prose-a:text-white text-white">
                    <Parse html={column.content} />
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </div>
      </div>
      <Container className="footerBottom max-lg:py-vertical-main">
        <div
          className={clsx(
            'gap-gutter gap-y-vertical-main lg:pt-vertical-main pb-gutter text-small grid grid-cols-2 border-b border-neutral-400 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]',
            (data?.footerPartners && data?.footerPartners.length === 0) ||
              !data?.footerPartners
              ? 'pb-vertical-main md:pb-vertical-small'
              : 'pb-gutter'
          )}
        >
          {data?.footerColumnsBottom?.map((column, index) => (
            <div className="" key={index}>
              <h2 className="!text-style-base mb-sm">{column.title}</h2>
              <div className="prose text-small">
                <Parse html={column.content} />
              </div>
            </div>
          ))}
          <div className="text-base max-sm:col-span-2">
            <h2 className="mb-md !text-style-base">{t('follow_us')}</h2>
            <div className="flex flex-wrap gap-4">
              {data?.footerSocials?.map((link, index) => (
                <Button key={index} data={link} />
              ))}
            </div>
            <div className="mt-md text-small flex flex-wrap gap-4">
              {data?.footerQuickLinks?.map((link, index) => (
                <Link
                  key={index}
                  data={link}
                  className="underline hover:no-underline"
                />
              ))}
            </div>
          </div>
          <div>
            <CodeSnippet
              html={`<div id="swissqq-seal-container" className="w-[190px]"></div>
                <script
                async defer
                src="/swissqq.js?lang=de&align=landscape&view=all&size=small&style=12&link=1"
              />`}
            />
          </div>
        </div>
        {data?.footerPartners && data?.footerPartners?.length > 0 && (
          <div className="py-lg max-lg:mb-md flex justify-between gap-4">
            {data?.footerPartners?.map((partner, index) => (
              <div key={index} className="flex items-center">
                {partner?.link ? (
                  <a
                    href={partner?.link}
                    target="_blank"
                    className="flex max-w-3xl items-center"
                  >
                    <Image data={partner?.image} />
                  </a>
                ) : (
                  <Image data={partner?.image} />
                )}
              </div>
            ))}
          </div>
        )}
      </Container>
    </Section>
  )
}

export default Footer
